import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ProcessorDetails } from '@/lib/api/processors';
import { formatMoney } from '@/lib/money';

interface RouteSenseConfigurationCardProps {
  processor: ProcessorDetails;
}

const formatDate = (date: Date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const RouteSenseConfigurationCard = ({ processor }: RouteSenseConfigurationCardProps) => {
  const { t } = useTranslation('processors');
  const options = processor.options as Record<string, unknown> | null;
  const hasSecurityKey = Boolean(options?.securityKey);
  const testMode = options?.testMode === true;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>{t('routeSenseConfiguration')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <p className="text-muted-foreground text-sm">{t('securityKey')}</p>
            <p className="font-mono text-sm">{hasSecurityKey ? '••••••••••••••••' : t('notConfigured')}</p>
          </div>

          <div>
            <p className="text-muted-foreground text-sm">{t('environment')}</p>
            <Badge variant={testMode ? 'secondary' : 'default'}>
              {testMode ? t('sandbox') : t('production')}
            </Badge>
          </div>

          {processor.baseCurrency && (
            <div>
              <p className="text-muted-foreground text-sm">{t('baseCurrency')}</p>
              <p className="font-semibold">{processor.baseCurrency}</p>
            </div>
          )}

          {processor.monthlyProcessingCapacity !== null && (
            <div>
              <p className="text-muted-foreground text-sm">{t('monthlyCapacity')}</p>
              <p className="font-semibold">
                {formatMoney(processor.monthlyProcessingCapacity, processor.baseCurrency || 'USD')}
              </p>
            </div>
          )}

          <div>
            <p className="text-muted-foreground text-sm">{t('createdAt')}</p>
            <p className="font-semibold">{formatDate(processor.createdAt)}</p>
          </div>

          <div>
            <p className="text-muted-foreground text-sm">{t('updatedAt')}</p>
            <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
          </div>

          {processor.supportedCurrencies && processor.supportedCurrencies.length > 0 && (
            <div className="col-span-full">
              <p className="text-muted-foreground mb-2 text-sm">{t('supportedCurrencies')}</p>
              <div className="flex flex-wrap gap-2">
                {processor.supportedCurrencies.map((currency) => (
                  <Badge key={currency} variant="outline">
                    {currency}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
